<template>
  <main class="flex-grow-1 px-1 py-1">

    <!-- ===================== -->
    <!-- TOP STATS -->
    <!-- ===================== -->
    <div class="row g-3 mb-4">

      <!-- Profile Completion -->
      <div class="col-md-3">
      <div class="glassy-table border rounded-lg p-3 shadow-sm h-100">
        <small class="text-muted fw-bold d-block mb-2">PROFILE COMPLETION</small>

        <!-- Loading state -->
        <div v-if="profile_completion_loading" class="text-muted small py-2">
          <span class="spinner-border spinner-border-sm me-2" role="status"></span>
          Loading...
        </div>

        <template v-else>
          <h3 class="fw-bold mb-1">{{ profile_completion.completion_percentage }}%</h3>
          <small class="text-muted d-block mb-3">
            {{ profile_completion.unanswered_questions }} mandatory steps remaining
          </small>
          <div class="progress" style="height:6px;">
            <div
              class="progress-bar bg-success"
              :style="{ width: profile_completion.completion_percentage + '%' }">
            </div>
          </div>
        </template>

      </div>
    </div>

        <!-- Compliance -->
        <div class="col-md-3">
          <div class="glassy-table border rounded-lg p-3 shadow-sm h-100">
            <small class="text-muted fw-bold d-block mb-2">ON-GOING-COMPLIANCE</small>
            <h3 class="fw-bold text-success mb-1">{{ stats.compliance_rate_pct }}%</h3>
            <small class="text-muted d-block mb-3">{{ stats.compliant_rules_count }} of {{ stats.total_rules }} rules compliant</small>

            <div class="d-flex flex-wrap gap-2">
              <span class="badge bg-success text-white border border-success">{{ stats.compliant_rules_count }} Clear</span>
              <span class="badge bg-danger text-white border border-danger">{{ stats.overdue_tasks_count }} Non-Compliant</span>
              <span class="badge bg-warning text-white border border-warning">{{ stats.due_soon_tasks_count }} Pending</span>
            </div>
          </div>
        </div>

        <!-- Open Tasks -->
        <div class="col-md-3">
          <div class="glassy-table border rounded-lg p-3 shadow-sm h-100">
            <small class="text-muted fw-bold d-block mb-2">OPEN TASKS</small>
            <h3 class="fw-bold text-danger mb-1">{{ stats.open_tasks_count }}</h3>
            <small class="text-muted d-block mb-3">Tasks requiring action</small>

            <div class="d-flex flex-wrap gap-2">
              <span class="badge bg-danger text-white border border-danger">{{ stats.overdue_tasks_count }} Overdue</span>
              <span class="badge bg-warning text-white border border-warning">{{ stats.due_soon_tasks_count }} Due Soon</span>
            </div>
          </div>
        </div>

      <!-- Documents -->
      <div class="col-md-3">
        <div class="glassy-table border rounded-lg p-3 shadow-sm h-100">
          <small class="text-muted fw-bold d-block mb-2">DOCUMENTS</small>

          <!-- Loading state -->
          <div v-if="document_counts_loading" class="text-muted small py-2">
            <span class="spinner-border spinner-border-sm me-2" role="status"></span>
            Loading...
          </div>

          <!-- Loaded state -->
          <template v-else>
            <h3 class="fw-bold mb-1">
              {{ document_counts.total_documents }}
            </h3>
            <small class="text-muted d-block mb-3">
              Total documents across all categories
            </small>

            <div class="d-flex flex-wrap gap-2 mb-3">
              <span
                v-if="document_counts.application_documents > 0"
                class="badge bg-success text-white border border-success">
                {{ document_counts.application_documents }} Application
              </span>
              <span
                v-if="document_counts.submission_documents > 0"
                class="badge bg-primary text-white border border-primary">
                {{ document_counts.submission_documents }} Submission
              </span>
              <span
                v-if="document_counts.journal_documents > 0"
                class="badge bg-secondary text-white border border-secondary">
                {{ document_counts.journal_documents }} Journal
              </span>
              <span
                v-if="document_counts.additional_documents > 0"
                class="badge bg-info text-white border border-info">
                {{ document_counts.additional_documents }} Additional
              </span>
              <span
                v-if="document_counts.total_documents === 0"
                class="badge bg-danger text-white border border-danger">
                No Documents
              </span>
            </div>
          </template>
        </div>
      </div>

    </div>

    <!-- ===================== -->
    <!-- MAIN GRID -->
    <!-- ===================== -->
    <div class="row g-4">

      <!-- ACTION SECTION -->
      <div class="col-lg-8">
        <div class="glassy-table border rounded-lg shadow-sm h-100">

          <div
            class="d-flex justify-content-between align-items-center p-4 border-bottom cursor-pointer"
            role="button"
            @click="show_actions = !show_actions"
          >
            <h6 class="fw-bold m-0 text-danger">
              <i class="fas fa-circle fs-6 me-2"></i>
              What Needs To Happen Now
            </h6>
            <i
              class="fas text-muted transition"
              :class="show_actions ? 'fa-chevron-up' : 'fa-chevron-down'">
            </i>
          </div>

          <div v-if="show_actions" class="p-4">

              <div v-if="is_loading_tasks" class="text-center py-4">
                <div class="spinner-border text-primary spinner-border-sm" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-2 text-muted text-xs">Loading compliance requirements...</p>
              </div>

              <div v-else-if="tasks.length === 0" class="text-center py-4">
                <p class="text-muted text-xs">Nothing needs attending to at this moment.</p>
              </div>

              <div v-else>
              <div v-for="task in tasks" :key="task.id"
                   class="d-flex justify-content-between align-items-start border-bottom pb-4 mb-4 border-dark">

              <div class="d-flex gap-3">

                <!-- Status Dot -->
                <div class="mt-2">
                  <span
                    class="rounded-circle d-inline-block"
                    :class="{
                      'bg-danger':  task.priority === 'red',
                      'bg-warning': task.priority === 'orange'
                    }"
                    style="width:10px;height:10px;">
                  </span>
                </div>

                <!-- Task Info -->
                <div>
                  <h6 class="fw-bold mb-2">{{ task.title }}</h6>
                  <p class="text-dark small mb-3" style="max-width:500px;">
                    {{ task.description }}
                  </p>

                  <div class="d-flex flex-wrap gap-2">
                    <span
                      class="badge fw-semibold"
                      :class="{
                        'bg-danger text-white':  task.priority === 'red',
                        'bg-warning text-white': task.priority === 'orange'
                      }">
                      {{ task.deadline }}
                    </span>

                    <span
                      v-for="tag in task.tags"
                      :key="tag"
                      class="badge bg-primary text-white">
                      {{ tag }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Action Button -->
              <button
                class="btn btn-primary-dark text-xs rounded"
                :class="task.priority === 'red' ? 'btn-success' : 'btn-outline-secondary'">
                {{ task.btnText }}
              </button>

                </div>

                <!-- Additional Documents Message -->
                <div v-if="more_unanswered_count > 0" class="alert alert-info alert-sm py-2 px-3 mb-0">
                  <small class="m-0">
                    <strong>{{ more_unanswered_count }} more {{ more_unanswered_count === 1 ? 'document' : 'documents' }} awaiting response.</strong>
                    View all in the Compliance section.
                  </small>
                </div>
              </div>

          </div>
        </div>
      </div>

      <!-- RIGHT COLUMN -->
      <div class="col-lg-4">

        <!-- Supplier Details -->
        <div class="glassy-table border rounded-lg shadow-sm mb-4">
          <div
            class="d-flex justify-content-between align-items-center p-4 border-bottom cursor-pointer"
            role="button"
            @click="show_trust_details = !show_trust_details"
          >
            <h6 class="fw-bold m-0 text-primary-blue">
              <i class="fas fa-circle fs-6 me-2"></i>
              Entity Details
            </h6>
            <i
              class="fas text-muted transition"
              :class="show_trust_details ? 'fa-chevron-up' : 'fa-chevron-down'">
            </i>
          </div>

          <div v-if="show_trust_details" class="p-4">

            <div
              v-for="(val, label) in trust_details"
              :key="label"
              class="d-flex justify-content-between border-bottom border-dark py-2 small">
              <span class="text-dark">{{ label }}</span>
              <span
                class="fw-semibold"
                :class="label === 'Ind. Trustee' ? 'badge bg-success-subtle text-success border border-success' : ''">
                {{ val }}
              </span>
            </div>

          <div class="pt-3">
            <small class="text-dark d-block mb-2">Profile completion</small>

            <div class="d-flex align-items-center gap-2">
              <div class="progress w-100" style="height: 8px;">
                <div
                  class="progress-bar bg-success"
                  :style="{ width: profile_completion.completion_percentage + '%' }"
                ></div>
              </div>

              <span class="fw-bold text-success small">
                {{ profile_completion.completion_percentage }}%
              </span>
            </div>
          </div>
          </div>
        </div>

        <!-- Assets (hidden) -->
        <div class="glassy-table border rounded-lg shadow-sm p-4 d-none">
          <small class="fw-bold text-muted d-block mb-3">ASSETS HELD IN COMPANY</small>

          <div
            v-for="asset in assets"
            :key="asset.name"
            class="d-flex justify-content-between align-items-center border-bottom py-2 small border-dark">

            <div class="d-flex align-items-center gap-2">
              <div class="bg-primary text-white rounded-3 d-inline-flex p-2">
                <i :class="asset.icon" class="fs-6"></i>
              </div>
              <span>{{ asset.name }}</span>
            </div>

            <span class="fw-bold">
              {{ asset.count }} {{ asset.unit }}
            </span>
          </div>
        </div>

      </div>

    </div>

  </main>
</template>

<script>
import {
  COUNT_APPLICATION_DOCUMENTS_URL,
  COUNT_SUB_ENTITIES_URL,
  GET_ENTITY_COMPLIANCE_OVERVIEW_URL,
  get_headers, GET_PROFILE_COMPLETION_ID
} from '@/utils/constants/constants';
import alert_mixin from "@/utils/mixins/alert_mixin";
import axios from 'axios';

export default {
  name: 'TrustProfileOverviewDashboard',
  mixins: [alert_mixin],

  props: {
    entity_profile_id: {
      type: [String, Number],
      required: true,
    },
    application_form_id: {
      type: [String, Number],
      default: null,
    },
    application_id: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      show_actions: true,
      show_trust_details: true,

      // Sub-entity stats
      is_loading_stats: false,
      sub_entity_stats: {
        total: 0,
        active: 0,
      },

      // Document counts
      document_counts_loading: false,
      document_counts: {
        application_documents: 0,
        submission_documents: 0,
        journal_documents: 0,
        additional_documents: 0,
        total_documents: 0,
      },
      profile_completion_loading: false,
      profile_completion: {
        total_questions: 0,
        answered_questions: 0,
        unanswered_questions: 0,
        completion_percentage: 0,
      },

      is_loading_tasks: false,
      tasks: [],
      more_unanswered_count: 0,
      stats: {
        profile_completion_pct: 0,
        profile_steps_remaining: 0,
        compliance_rate_pct: 0,
        compliant_rules_count: 0,
        total_rules: 0,
        open_tasks_count: 0,
        overdue_tasks_count: 0,
        due_soon_tasks_count: 0,
        documents_uploaded: 0,
        documents_total: 0,
        documents_missing: 0,
        documents_pending: 0,
      },

        assets: [
          { icon: 'fas fa-home',       name: 'Residential Property', count: 3,  unit: 'properties' },
          { icon: 'fas fa-building',   name: 'Commercial Property',  count: 1,  unit: 'property'   },
          { icon: 'fas fa-chart-line', name: 'Listed Equities',      count: 12, unit: 'holdings'   },
        ],
      };
    },

    computed: {
      trust_details() {
        const entity_type_name = sessionStorage.getItem('entity_type_name') || '—';
        const registered = sessionStorage.getItem('date_created')     || '—';

        const employees_label = this.is_loading_stats
          ? 'Loading...'
          : this.sub_entity_stats.total > 0
            ? `${this.sub_entity_stats.total} registered`
            : 'No employees found';

        return {
          'Type': entity_type_name,
          'Employees':  employees_label,
          'Registered': registered,
        };
      },
    },

    mounted() {
      this.fetch_sub_entity_stats();
      this.fetch_document_counts();
      this.fetch_profile_completion();
      this.load_compliance_tasks();
    },

    watch: {
      entity_profile_id: {
        handler() {
          this.load_compliance_tasks();
          this.fetch_sub_entity_stats();
        }
      },

      application_form_id: {
        handler(newVal) {
          if (newVal) {
            this.fetch_profile_completion();
          }
        }
      },

      application_id: {
        handler(newVal) {
          if (newVal) {
            this.fetch_document_counts();
          } else {
            this.document_counts = {
              application_documents: 0,
              submission_documents: 0,
              journal_documents: 0,
              additional_documents: 0,
              total_documents: 0,
            };
          }
        }
      }
    },

    methods: {
      fetch_sub_entity_stats() {
        const entity_profile_id = this.entity_profile_id || sessionStorage.getItem('entity_profile_id');
        if (!entity_profile_id) return;

        this.is_loading_stats = true;

        axios.get(COUNT_SUB_ENTITIES_URL, {
            headers: get_headers(),
            params: { entity_profile_id },
          })
          .then((response) => {
            if (response.data?.status === 'success') {
              this.sub_entity_stats.total  = response.data.sub_entity_count        || 0;
              this.sub_entity_stats.active = response.data.active_sub_entity_count || 0;
            }
          })
          .catch((err) => {
            this.show_error('Failed to load sub-entity stats:', err);
          })
          .finally(() => {
            this.is_loading_stats = false;
          });
      },

    fetch_document_counts() {
      const application_id = Number(this.application_id) || Number(sessionStorage.getItem('application_id') || 0);
      if (!application_id) {
        return;
      }

      this.document_counts_loading = true;

      axios.get(COUNT_APPLICATION_DOCUMENTS_URL, {
          headers: get_headers(),
          params: { application_id, entity_profile_id: this.entity_profile_id },
        })
        .then((response) => {
          const body = response.data;

          if (body?.status === 'success' && body?.data) {
            this.document_counts = { ...body.data };
          } else {
            this.show_error('Unexpected response from document count API:', body);
          }
        })
        .catch((error) => {
          this.show_error(
            'Error fetching document counts:',
            error.response?.data?.message || error.message
          );
        })
        .finally(() => {
          this.document_counts_loading = false;
        });
    },

    fetch_profile_completion() {
      const application_form_id = Number(this.application_form_id) || Number(sessionStorage.getItem("application_form_id"));
      if (!application_form_id) return;

      this.profile_completion_loading = true;

      axios.get(GET_PROFILE_COMPLETION_ID, {
          headers: get_headers(),
          params: { application_form_id},
        })
        .then((response) => {
          if (response.data?.status === 'success' && response.data?.data) {
            this.profile_completion = { ...response.data.data };
          } else {
            this.show_error('Unexpected response from profile completion API:', response.data);
          }
        })
        .catch((error) => {
          this.show_error(
            'Error fetching profile completion:',
            error.response?.data?.message || error.message
          );
        })
        .finally(() => {
          this.profile_completion_loading = false;
        });
      },
    async load_compliance_tasks() {
      this.is_loading_tasks = true;
      this.tasks = [];
      this.more_unanswered_count = 0;

      try {
        const response = await axios.get(GET_ENTITY_COMPLIANCE_OVERVIEW_URL, {
          params: {
            entity_profile_id: this.entity_profile_id,
          },
          headers: get_headers(),
        });

        if (response.data.status === 'success') {
          const overview_data = response.data.data;
          const actionable_items = overview_data.actionable_items || [];

          this.tasks = actionable_items.map(doc => this.format_task(doc)).slice(0, 3);
          this.more_unanswered_count = Math.max(0, actionable_items.length - 3);

          this.calculate_stats(overview_data, actionable_items);
        }
      } catch (error) {
        console.warn('Error loading compliance tasks:', error);
        this.tasks = [];
      } finally {
        this.is_loading_tasks = false;
      }
    },

    calculate_stats(overview_data, actionable_items) {
      const total_q = overview_data.total_questions || 0;
      const total_ans = overview_data.total_answered || 0;

      this.stats.documents_total = total_q;
      this.stats.documents_uploaded = total_ans;
      this.stats.profile_steps_remaining = total_q - total_ans;

      this.stats.profile_completion_pct = total_q > 0 ? Math.round((total_ans / total_q) * 100) : 0;

      const rules = overview_data.rules || [];
      this.stats.total_rules = rules.length;
      this.stats.compliant_rules_count = rules.filter(r => r.answered_count === r.total_questions).length;
      this.stats.compliance_rate_pct = rules.length > 0 ? Math.round((this.stats.compliant_rules_count / rules.length) * 100) : 0;

      this.stats.open_tasks_count = actionable_items.length;
      this.stats.overdue_tasks_count = actionable_items.filter(item => item.priority === 'red').length;
      this.stats.due_soon_tasks_count = actionable_items.filter(item => item.priority === 'orange').length;

      this.stats.documents_missing = this.stats.overdue_tasks_count;
      this.stats.documents_pending = this.stats.due_soon_tasks_count;
    },

    format_task(doc) {
      return {
        id: doc.compliance_field_id,
        compliance_field_id: doc.compliance_field_id,
        priority: doc.priority || 'orange',
        title: doc.title,
        description: doc.description,
        deadline: doc.deadline || 'Awaiting response',
        deadlineClass: doc.priority === 'red' ? 'tag-red' : 'tag-orange',
        tags: [doc.rule_name || 'Compliance'],
        btnText: 'Answer Question',
        btnClass: doc.priority === 'red' ? 'btn-success' : 'btn-outline-secondary',
      };
    },
  },
};
</script>
